/**
 * A walk-in's name and phone, for the receipt only — the path for anyone who may not search the
 * customer list. The list is a directory of other people's phone numbers, which is why it stays
 * behind `customer.view`; typing the name of the person standing at the counter exposes nobody,
 * so it needs no permission at all. Nothing is saved to the customer list from here.
 */

import { useState } from 'react';

import { Button, Field, Sheet } from '@/shared/ui';

export type CustomerNameSheetProps = {
  open: boolean;
  initialName: string | null;
  initialPhone: string | null;
  onSave: (name: string, phone: string | null) => void;
  onDismiss: () => void;
};

export function CustomerNameSheet({
  open,
  initialName,
  initialPhone,
  onSave,
  onDismiss,
}: CustomerNameSheetProps) {
  return (
    <Sheet open={open} onDismiss={onDismiss} title="Customer on the receipt">
      {/* Mounted only while open, so each opening starts from what the cart holds, not from a draft the
          person abandoned last time. */}
      {open && (
        <CustomerNameForm initialName={initialName} initialPhone={initialPhone} onSave={onSave} />
      )}
    </Sheet>
  );
}

function CustomerNameForm({
  initialName,
  initialPhone,
  onSave,
}: Pick<CustomerNameSheetProps, 'initialName' | 'initialPhone' | 'onSave'>) {
  const [name, setName] = useState(initialName ?? '');
  const [phone, setPhone] = useState(initialPhone ?? '');

  const trimmedName = name.trim();
  const trimmedPhone = phone.trim();

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-content-muted">
        Printed on the receipt so the customer can prove the purchase later — the phone number with
        its middle hidden. It is not added to your customer list.
      </p>

      <Field
        label="Name"
        placeholder="e.g. Grace Wanjiku"
        value={name}
        maxLength={120}
        autoFocus
        onChange={(event) => {
          setName(event.target.value);
        }}
      />

      <Field
        label="Phone"
        placeholder="Optional"
        inputMode="tel"
        value={phone}
        maxLength={30}
        onChange={(event) => {
          setPhone(event.target.value);
        }}
      />

      <Button
        size="lg"
        fullWidth
        disabled={trimmedName.length === 0}
        onClick={() => {
          onSave(trimmedName, trimmedPhone.length > 0 ? trimmedPhone : null);
        }}
      >
        Add to sale
      </Button>
    </div>
  );
}
